import { Box, Button, HStack, IconButton, Tooltip } from '@chakra-ui/react';
import { ChevronLeft, ChevronRight, ChevronsRight, CheckCircle2, XCircle } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';

import { AssessmentOutcome, SlideKind } from '@duke-hq/svc-api-db';
import { AssessmentOutcomeSchema } from '@duke-hq/svc-api-db/schemas/enums/AssessmentOutcome.schema';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';

import { AssessmentButtonGroup } from './AssessmentButtonGroup';
import { MarkingStatusBadge } from './MarkingStatusBadge';
import { OutcomeOverrideMenu } from './OutcomeOverrideMenu';
import { useMarkingContext } from './context/MarkingContext';
import { markingStatus } from './utils';

function MarkingSlot({ children }: { children: React.ReactNode }) {
  return (
    <Box
      w={pxToRem(220)}
      h={pxToRem(40)}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {children}
    </Box>
  );
}

function OverviewNav() {
  const { assessmentSubmission, overviewState, onPrevious } = useMarkingContext();

  const isAlreadyAssessed =
    assessmentSubmission.outcome !== null &&
    assessmentSubmission.outcome !== AssessmentOutcome.PENDING;
  const showOverrideMenu = !isAlreadyAssessed && overviewState.canOverride;
  const isSatisfactory =
    overviewState.selectedOutcome === AssessmentOutcomeSchema.enum.SATISFACTORY;

  return (
    <HStack gap={2} flexShrink={0}>
      <Tooltip label={t`Previous slide`} placement="top" shouldWrapChildren>
        <IconButton
          aria-label={t`Previous slide`}
          icon={<ChevronLeft />}
          variant="outline"
          colorScheme="purple"
          size="md"
          onClick={onPrevious}
        />
      </Tooltip>

      {showOverrideMenu ? (
        <MarkingSlot>
          <OutcomeOverrideMenu
            selectedOutcome={overviewState.selectedOutcome}
            onSelect={overviewState.setSelectedOutcome}
          />
        </MarkingSlot>
      ) : null}

      {!isAlreadyAssessed ? (
        <Button
          variant="solid"
          colorScheme={isSatisfactory ? 'green' : 'red'}
          size="md"
          leftIcon={
            isSatisfactory ? <CheckCircle2 size={iconSize.md} /> : <XCircle size={iconSize.md} />
          }
          onClick={overviewState.handleSubmit}
          isDisabled={!overviewState.isValid}
          isLoading={overviewState.isSubmitting}
          loadingText={t`Submitting`}
        >
          <Trans>Submit</Trans>
        </Button>
      ) : null}
    </HStack>
  );
}

function SlideNav() {
  const {
    currentSlide,
    currentSlideIndex,
    currentAnswer,
    currentAnswerHasFeedback,
    isMarkingPending,
    markingLoadingType,
    onMarkCorrect,
    onMarkRevision,
    assessmentSubmission,
    onNext,
    onNextUnmarked,
    onPrevious,
  } = useMarkingContext();

  const isAutoGraded = currentSlide?.kind === SlideKind.QUESTION_GROUP;
  const needsRevisionFeedback = currentAnswer?.correct === false && !currentAnswerHasFeedback;
  const nextDisabled = !isAutoGraded && (currentAnswer?.correct == null || needsRevisionFeedback);
  const status = markingStatus(currentAnswer?.correct);
  const isAlreadyAssessed = assessmentSubmission.assessed_at !== null;

  return (
    <HStack gap={2} flexShrink={0}>
      <Tooltip label={t`Previous slide`} placement="top" shouldWrapChildren>
        <IconButton
          aria-label={t`Previous slide`}
          icon={<ChevronLeft />}
          variant="outline"
          colorScheme="purple"
          size="md"
          isDisabled={currentSlideIndex === 0}
          onClick={onPrevious}
        />
      </Tooltip>

      {currentSlide?.kind !== null ? (
        <MarkingSlot>
          {currentSlide?.kind === SlideKind.QUESTION_GROUP ? (
            <MarkingStatusBadge
              status={status}
              correctLabel={t`Auto-assess · Correct`}
              incorrectLabel={t`Auto-assess · Incorrect`}
            />
          ) : currentSlide?.kind === SlideKind.FORM ? (
            <MarkingStatusBadge
              status={status}
              correctLabel={t`Marked · Correct`}
              incorrectLabel={t`Marked · Needs revision`}
            />
          ) : (
            <Box
              borderWidth="1px"
              borderColor="border.default"
              borderRadius="md"
              p={1}
              w="100%"
              h="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <AssessmentButtonGroup
                status={status}
                showBoth
                disabled={isAlreadyAssessed || isMarkingPending}
                isLoading={isMarkingPending}
                loadingType={markingLoadingType}
                onRevisionClick={onMarkRevision}
                onCorrectClick={onMarkCorrect}
              />
            </Box>
          )}
        </MarkingSlot>
      ) : null}

      <HStack gap={0} flexShrink={0}>
        <Tooltip label={t`Next slide`} placement="top" shouldWrapChildren>
          <IconButton
            aria-label={t`Next slide`}
            icon={<ChevronRight />}
            variant="outline"
            colorScheme="purple"
            size="md"
            onClick={onNext}
            isDisabled={nextDisabled}
            borderRightRadius={0}
          />
        </Tooltip>

        <Tooltip
          label={t`Skips to the next unassessed slide`}
          placement="top-end"
          shouldWrapChildren
        >
          <Button
            variant="outline"
            colorScheme="purple"
            size="md"
            onClick={onNextUnmarked}
            rightIcon={<ChevronsRight />}
            iconSpacing={1}
            px={3}
            isDisabled={nextDisabled}
            borderLeftRadius={0}
            borderLeftWidth={0}
          >
            <Trans>Skip</Trans>
          </Button>
        </Tooltip>
      </HStack>
    </HStack>
  );
}

export function NavControls() {
  const { isOverviewScreen } = useMarkingContext();
  return isOverviewScreen ? <OverviewNav /> : <SlideNav />;
}
